import { useState, useCallback } from "react";
import {
  Code2, Zap, Target, Trophy, Bug,
  Cpu, Star, Flame, Braces, Terminal,
  Hash, GitBranch, Layers, Rocket, Lightbulb,
  Timer, Database, Award, Activity, Network,
  Search, Settings, Shuffle, RefreshCw, BarChart2,
  Shield, CheckCircle, Lock, Eye, Compass,
} from "lucide-react";

const ICONS_CONFIG = [
  { id: "code",      Icon: Code2,        color: "#60a5fa", msg: "Clean code enjoyer! 🧹"           },
  { id: "zap",       Icon: Zap,          color: "#facc15", msg: "Speed demon unlocked! ⚡"          },
  { id: "target",    Icon: Target,       color: "#f472b6", msg: "Target acquired! 🎯"               },
  { id: "trophy",    Icon: Trophy,       color: "#fb923c", msg: "Future champion! 🏆"               },
  { id: "bug",       Icon: Bug,          color: "#4ade80", msg: "Bug squashed! +1 debug skill 🐛"   },
  { id: "cpu",       Icon: Cpu,          color: "#c084fc", msg: "Big brain time! 🤖"                },
  { id: "star",      Icon: Star,         color: "#fbbf24", msg: "Shooting for the stars! ⭐"        },
  { id: "flame",     Icon: Flame,        color: "#f97316", msg: "You're on fire today! 🔥"          },
  { id: "braces",    Icon: Braces,       color: "#2dd4bf", msg: "Brackets balanced! { }"           },
  { id: "terminal",  Icon: Terminal,     color: "#818cf8", msg: "Runs in O(1) in your mind! 💻"     },
  { id: "hash",      Icon: Hash,         color: "#e879f9", msg: "Hash map? Always! #️⃣"              },
  { id: "gitbranch", Icon: GitBranch,    color: "#34d399", msg: "Divide & conquer unlocked! 🌿"    },
  { id: "layers",    Icon: Layers,       color: "#93c5fd", msg: "Stack overflow? Not today! 📚"    },
  { id: "rocket",    Icon: Rocket,       color: "#fb7185", msg: "Launching to the leaderboard! 🚀" },
  { id: "bulb",      Icon: Lightbulb,    color: "#fde68a", msg: "Eureka moment! 💡"                },
  { id: "timer",     Icon: Timer,        color: "#67e8f9", msg: "Beat the clock! ⏱️"               },
  { id: "database",  Icon: Database,     color: "#a78bfa", msg: "Data structures mastered! 🗄️"    },
  { id: "award",     Icon: Award,        color: "#fbbf24", msg: "Achievement unlocked! 🎖️"         },
  { id: "activity",  Icon: Activity,     color: "#6ee7b7", msg: "Complexity: O(amazing)! 📈"       },
  { id: "network",   Icon: Network,      color: "#f9a8d4", msg: "Graph traversal complete! 🕸️"    },
  { id: "search",    Icon: Search,       color: "#7dd3fc", msg: "Binary search activated! 🔍"      },
  { id: "settings",  Icon: Settings,     color: "#cbd5e1", msg: "Optimized to perfection! ⚙️"      },
  { id: "shuffle",   Icon: Shuffle,      color: "#f0abfc", msg: "Randomized? Still O(n log n)! 🔀" },
  { id: "refresh",   Icon: RefreshCw,    color: "#86efac", msg: "Retrying... recursion vibes! 🔄"  },
  { id: "barchart",  Icon: BarChart2,    color: "#fdba74", msg: "Rating going up! 📊"               },
  { id: "shield",    Icon: Shield,       color: "#38bdf8", msg: "Edge cases: handled! 🛡️"          },
  { id: "check",     Icon: CheckCircle,  color: "#4ade80", msg: "Accepted! ✅"                      },
  { id: "lock",      Icon: Lock,         color: "#c4b5fd", msg: "Premium unlocked... jk 🔓"        },
  { id: "eye",       Icon: Eye,          color: "#fb7185", msg: "Always reviewing code! 👁️"        },
  { id: "compass",   Icon: Compass,      color: "#5eead4", msg: "Navigating the solution! 🧭"      },
];

// 32 positions — original margins + extra interior columns for full coverage.
// Note: ICONS_CONFIG only has 30 entries, so sprite indices 30/31 must wrap
// around safely rather than indexing ICONS_CONFIG directly (see iconIndex
// below) — that mismatch was the cause of the destructure crash.
const SPRITES = [
  // far-left & far-right margins (original, sped up)
  { top: "5%",  left: "2%",   anim: "float-a", delay: "0s",    dur: "5s"   },
  { top: "8%",  left: "91%",  anim: "float-b", delay: "1.2s",  dur: "6s"   },
  { top: "14%", left: "8.5%", anim: "float-c", delay: "2.4s",  dur: "5.5s" },
  { top: "16%", left: "83%",  anim: "float-a", delay: "0.7s",  dur: "7s"   },
  { top: "26%", left: "1.5%", anim: "float-b", delay: "0.6s",  dur: "5s"   },
  { top: "24%", left: "93%",  anim: "float-c", delay: "2.1s",  dur: "6.5s" },
  { top: "33%", left: "9%",   anim: "float-a", delay: "1.8s",  dur: "4.8s" },
  { top: "35%", left: "81%",  anim: "float-b", delay: "0.9s",  dur: "5.2s" },
  { top: "44%", left: "2.5%", anim: "float-c", delay: "3.1s",  dur: "6s"   },
  { top: "43%", left: "90%",  anim: "float-a", delay: "1.5s",  dur: "4.8s" },
  { top: "52%", left: "8%",   anim: "float-b", delay: "0.3s",  dur: "7s"   },
  { top: "53%", left: "82%",  anim: "float-c", delay: "2.6s",  dur: "5.8s" },
  { top: "63%", left: "1.8%", anim: "float-a", delay: "1.1s",  dur: "6.5s" },
  { top: "62%", left: "92%",  anim: "float-b", delay: "3.4s",  dur: "4.5s" },
  { top: "71%", left: "7.5%", anim: "float-c", delay: "1.9s",  dur: "5s"   },
  { top: "70%", left: "83%",  anim: "float-a", delay: "0.5s",  dur: "6s"   },
  { top: "80%", left: "3%",   anim: "float-b", delay: "2.8s",  dur: "5.3s" },
  { top: "79%", left: "88%",  anim: "float-c", delay: "1.3s",  dur: "4.7s" },
  { top: "88%", left: "9%",   anim: "float-a", delay: "0.4s",  dur: "6.8s" },
  { top: "87%", left: "82%",  anim: "float-b", delay: "2.2s",  dur: "5.1s" },
  // near-interior columns (~15–20% and ~76–82%)
  { top: "10%", left: "16%",  anim: "float-c", delay: "1.5s",  dur: "4.5s" },
  { top: "11%", left: "78%",  anim: "float-a", delay: "0.3s",  dur: "5.3s" },
  { top: "21%", left: "20%",  anim: "float-b", delay: "2.2s",  dur: "6.1s" },
  { top: "22%", left: "74%",  anim: "float-c", delay: "1.1s",  dur: "4.9s" },
  { top: "40%", left: "17%",  anim: "float-a", delay: "0.8s",  dur: "5.6s" },
  { top: "38%", left: "77%",  anim: "float-b", delay: "2.7s",  dur: "4.3s" },
  { top: "55%", left: "15%",  anim: "float-c", delay: "1.6s",  dur: "5.9s" },
  { top: "57%", left: "79%",  anim: "float-a", delay: "3.0s",  dur: "4.6s" },
  { top: "68%", left: "19%",  anim: "float-b", delay: "0.5s",  dur: "5.7s" },
  { top: "66%", left: "76%",  anim: "float-c", delay: "2.1s",  dur: "4.2s" },
  { top: "77%", left: "13%",  anim: "float-a", delay: "1.4s",  dur: "6.4s" },
  { top: "78%", left: "80%",  anim: "float-b", delay: "0.9s",  dur: "5.0s" },
];

export default function FloatingIcons() {
  // Tracks which sprite indices are currently in "caught" burst state
  const [caught, setCaught] = useState(new Set());
  // { msg, x, y } — position in viewport coords
  const [toast, setToast] = useState(null);

  const handleCatch = useCallback((e, idx) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const iconIndex = idx % ICONS_CONFIG.length;

    setToast({
      msg: ICONS_CONFIG[iconIndex].msg,
      x: rect.left + rect.width / 2,
      y: rect.top - 46,
      // key ensures the animation re-runs even if same icon is caught again
      key: Date.now(),
    });

    setCaught((prev) => new Set([...prev, idx]));

    // Icon reappears after 2 s
    setTimeout(() => {
      setCaught((prev) => {
        const next = new Set(prev);
        next.delete(idx);
        return next;
      });
    }, 2000);

    // Toast disappears after 1.8 s (matches animation duration)
    setTimeout(() => setToast(null), 1800);
  }, []);

  return (
    <>
      {/* Non-blocking overlay — container itself doesn't capture pointer events */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 1, pointerEvents: "none" }}
        aria-hidden="true"
      >
        {SPRITES.map((sprite, idx) => {
          // ICONS_CONFIG has fewer entries than SPRITES — wrap the index so
          // every position gets a valid icon instead of `undefined`.
          const iconIndex = idx % ICONS_CONFIG.length;
          const { id, Icon, color } = ICONS_CONFIG[iconIndex];
          const isCaught = caught.has(idx);

          return (
            <button
              key={`${id}-${idx}`}
              type="button"
              className={`fi-sprite${isCaught ? " fi-caught" : ""}`}
              style={{
                top: sprite.top,
                left: sprite.left,
                color,
                opacity: 0.85,
                filter: `drop-shadow(0 0 4px ${color}80)`,
                animation: `${sprite.anim} ${sprite.dur} ease-in-out ${sprite.delay} infinite`,
                background: "none",
                border: "none",
                padding: 0,
              }}
              onClick={(e) => handleCatch(e, idx)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleCatch(e, idx); }}
              aria-label={`Catch the floating ${id} icon`}
            >
              <div className="fi-inner" style={{ transform: "scale(1.15)" }}>
                <Icon size={19} strokeWidth={1.5} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Catch toast */}
      {toast && (
        <div
          key={toast.key}
          className="fi-toast"
          style={{ left: toast.x, top: toast.y }}
        >
          {toast.msg}
        </div>
      )}
    </>
  );
}